import React, { useCallback, useContext, useEffect } from "react";
import { toast } from "react-toastify";

import { AuthContext } from "../../../Auth/AuthContext";
import { i18n } from "../../../../translate/i18n";
import { useSocketAuthenticated } from "../../SocketContext";
import api from "../../../../services/api";

import { useGlobalRegisters } from "../../../GlobalRegisters/GlobalRegistersContext";

const SocketQueueOverrides = () => {
  //  ***************
  //  ** Variables **
  //  ***************
  const { getSocket } = useSocketAuthenticated();
  const { user } = useContext(AuthContext);

  const { getSetting, isLoading, queueOverridesDispatch } = useGlobalRegisters();
  const isQueueOverrideEnabled = !isLoading && getSetting("forceAcceptFirstTicket") === "enabled";



  //  ***************
  //  ** Callbacks **
  //  ***************
  const handleQueueOverrideAsResolverUser = useCallback((data) => {
    const isTenantIdValid = `${api.defaults.headers.tenantId}` === `${data.tenantId}`;
    if (!isTenantIdValid) return;

    if (data.action === "upsert") {
      queueOverridesDispatch({ type: "UPDATE_QUEUE_OVERRIDE", payload: data.queueOverride });
    }

    if (data.action === "delete") {
      queueOverridesDispatch({ type: "DELETE_QUEUE_OVERRIDE", payload: data.queueOverride.id });
    }
  }, [queueOverridesDispatch]);

  const handleQueueOverrideAsRequestUser = useCallback((data) => {
    const isTenantIdValid = `${api.defaults.headers.tenantId}` === `${data.tenantId}`;
    if (!isTenantIdValid) return;

    const isQueueOverrideRelatedToUser = user.id === data.queueOverride.requestedByUserId;
    if (!isQueueOverrideRelatedToUser) return;

    if (data.action === "upsert") {
      queueOverridesDispatch({ type: "UPDATE_QUEUE_OVERRIDE", payload: data.queueOverride });

      if (data.queueOverride.status === "approved") {
        toast.success(i18n.t("queueOverrideListModal.toasts.approvedAuthorization").replace("<ticketId>", data.queueOverride.ticketId));
      }
      
      if (data.queueOverride.status === "rejected") {
        toast.warning(i18n.t("queueOverrideListModal.toasts.rejectedAuthorization").replace("<ticketId>", data.queueOverride.ticketId));
      }
    }

    if (data.action === "delete") {
      queueOverridesDispatch({ type: "DELETE_QUEUE_OVERRIDE", payload: data.queueOverride.id });
    }
  }, [queueOverridesDispatch, user.id]);

  const removeListeners = useCallback((socket) => {
    socket.off("queueOverrideAsResolverUser", handleQueueOverrideAsResolverUser);
    socket.off("queueOverrideAsRequestUser", handleQueueOverrideAsRequestUser);
  }, [handleQueueOverrideAsResolverUser, handleQueueOverrideAsRequestUser]);

  const createListeners = useCallback((socket) => {
    if (user.canAuthorizeQueueOverride) {
      socket.on("queueOverrideAsResolverUser", handleQueueOverrideAsResolverUser);
    }
    else {
      socket.on("queueOverrideAsRequestUser", handleQueueOverrideAsRequestUser);
    }
  }, [user.canAuthorizeQueueOverride, handleQueueOverrideAsResolverUser, handleQueueOverrideAsRequestUser]);



  //  *****************
  //  ** Use Effects **
  //  *****************
  useEffect(() => {
    const socket = getSocket();
    if (!socket) return;

    removeListeners(socket);

    const isUserOnline = Object.keys(user).length > 0;
    if (!isQueueOverrideEnabled || !isUserOnline) return;

    createListeners(socket);
    return () => removeListeners(socket);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoading, user, isQueueOverrideEnabled, createListeners, removeListeners]);
  


  //  ************
  //  ** Return **
  //  ************
  return (<></>);
};

export default SocketQueueOverrides;
